import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AgeChip,
  Banner,
  Button,
  Card,
  ChecklistRow,
  ChecklistStack,
  Collapsible,
  CountBadge,
  Crest,
  CrestPair,
  DefinitionList,
  DefinitionRow,
  IdChip,
  KpiCell,
  KpiStrip,
  LifecycleRail,
  MarketRow,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  ProgressRing,
  REWARD_LABELS,
  RewardPill,
  StatusPill,
  Table,
  TCell,
  THead,
  TRow,
  Tabs,
  useCollapsibleSections,
  VerdictBadge,
  type Verdict,
} from '@cou/admin-ui';
import {
  defaultContestsClient,
  type AdminContestWithEntryCount,
  type ContestsClient,
} from '../contests/contests-client';
import {
  defaultCatalogueClient,
  type CatalogueClient,
  type CuratedEvent,
  type CuratedMarket,
} from '../contests/catalogue-client';
import { defaultConfigClient, type ConfigClient } from '../config/config-client';
import { payoutMatrixSegments } from '../contests/payout-matrix';
import { formatRewardValue, toRewardType, type RewardCell } from '../contests/reward-cell';
import { contestPillState } from '../contests/state-pill';
import { buildScheduleRail } from '../contests/schedule-rail';
import {
  computeReadiness,
  previewFinaliseGuards,
  type CuratedMarketLine,
  type ReadinessKey,
  type ReadinessLoading,
} from '../contests/readiness';
import { formatDateParts } from '../contests/format-date';
import { formatDuration, timeInStatusMs } from '../contests/duration';
import { defaultEntriesClient, type EntriesClient } from '../entries/entries-client';
import { defaultAwardsClient, type AwardsClient } from '../awards/awards-client';
import { ContestEntriesTab } from './ContestEntriesTab';
import { ContestAwardsTab } from './ContestAwardsTab';
import { formatEventMeta, hasQuote } from '../catalogue/format';
import { crestsForEventName, lookupTeam } from '../catalogue/teams';
import { useAdminAuth } from '../session/admin-auth-context';
import { AdminShell } from '../shell/AdminShell';

export interface ContestDetailScreenProps {
  /** Injected for tests; defaults to the real API-09/16/10/22/23 clients. */
  readonly contestsClient?: ContestsClient;
  readonly catalogueClient?: CatalogueClient;
  readonly configClient?: ConfigClient;
  readonly entriesClient?: EntriesClient;
  readonly awardsClient?: AwardsClient;
}

type TabKey = 'overview' | 'slate' | 'prizes' | 'rules' | 'entries' | 'awards';

function toReadinessLine(market: CuratedMarket): CuratedMarketLine {
  return {
    id: market.id,
    name: market.name,
    line: market.line,
    // Readiness/finalise guards run only for a draft Contest, whose markets come from the live
    // path and always carry a verdict; the frozen snapshot (finalised) has none, so default it.
    verdict: market.verdict ?? 'selectable',
  };
}

/** Maps a readiness checklist item to the contest-editor setup-rail step that fixes it. */
function readinessEditorStep(key: ReadinessKey): 'basic-info' | 'slate' | 'prizes' {
  switch (key) {
    case 'campaignId':
    case 'round':
      return 'basic-info';
    case 'slateCurated':
    case 'halfPointLines':
      return 'slate';
    case 'payoutMatrixComplete':
      return 'prizes';
  }
}

export function ContestDetailScreen({
  contestsClient = defaultContestsClient,
  catalogueClient = defaultCatalogueClient,
  configClient = defaultConfigClient,
  entriesClient = defaultEntriesClient,
  awardsClient = defaultAwardsClient,
}: ContestDetailScreenProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { claims, getToken } = useAdminAuth();
  const canManage = claims?.permissions.includes('contests:manage') ?? false;
  // Retry is its own permission (admin-ui.md matrix): an operator can read this Contest's Awards
  // without being able to act on them.
  const canRetryAwards = claims?.permissions.includes('awards:retry') ?? false;
  const [contest, setContest] = useState<AdminContestWithEntryCount | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');
  const [curatedMarkets, setCuratedMarkets] = useState<readonly CuratedMarket[]>([]);
  const [curatedEvents, setCuratedEvents] = useState<readonly CuratedEvent[]>([]);
  const [marketsLoading, setMarketsLoading] = useState(true);
  const [segmentRegistry, setSegmentRegistry] = useState<Record<string, string>>({});
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [finaliseModalOpen, setFinaliseModalOpen] = useState(false);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const curation = contest?.curation ?? null;
  const finalisedAt = contest?.finalisedAt ?? null;

  useEffect(() => {
    const token = getToken();
    if (token === null || id === undefined) return;
    let cancelled = false;
    void (async () => {
      const result = await contestsClient.getContest(token, id);
      if (cancelled) return;
      if (result.ok) {
        setContest(result.contest);
        setLoadError(null);
      } else {
        setLoadError(`Could not load this contest (${result.code ?? result.status}).`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contestsClient, getToken, id]);

  // The Slate/Overview per-prop rows. The source splits on finalisation state (CAE-485):
  //   • finalised → the frozen `contest_*` snapshot, immune to any catalogue-sync prune of the
  //     curated Live-catalogue rows (which used to blank the list out from under a finalised
  //     Contest). Frozen rows carry no live Line/verdict — the slate is immutable.
  //   • still draft → the Live catalogue, the only source for the half-point-line and
  //     suspended-prop readiness/finalise-guard checks, since AdminContest.curation carries only
  //     pointers (see contests/readiness.ts).
  // Keyed on `finalisedAt`/`curation`/`id` rather than the whole `contest` so an unrelated
  // contest-state change doesn't re-issue this fetch.
  useEffect(() => {
    const token = getToken();
    if (token === null || id === undefined) return;
    let cancelled = false;

    if (finalisedAt !== null) {
      setMarketsLoading(true);
      void (async () => {
        const slate = await catalogueClient.getContestSlate(token, id);
        if (cancelled) return;
        setCuratedEvents(slate.events);
        setCuratedMarkets(slate.markets);
        setMarketsLoading(false);
      })();
      return () => {
        cancelled = true;
      };
    }

    if (curation === null || curation.marketIds.length === 0) {
      setCuratedMarkets([]);
      setCuratedEvents([]);
      setMarketsLoading(false);
      return;
    }

    setMarketsLoading(true);
    void (async () => {
      const [markets, events] = await Promise.all([
        catalogueClient.getCuratedMarkets(token, curation.eventIds, curation.marketIds),
        catalogueClient.getCuratedEvents(token, curation.eventIds),
      ]);
      if (cancelled) return;
      setCuratedMarkets(markets);
      setCuratedEvents(events);
      setMarketsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogueClient, getToken, id, finalisedAt, curation]);

  // The segment registry doesn't depend on the contest at all — gate only on having a token and
  // an id to load for, so this fetch runs in parallel with the contest fetch instead of behind it.
  useEffect(() => {
    const token = getToken();
    if (token === null || id === undefined) return;
    let cancelled = false;
    void (async () => {
      const result = await configClient.getConfig(token);
      if (cancelled) return;
      if (result.ok) {
        const registry = result.config['segment_registry'];
        if (registry && typeof registry === 'object') {
          setSegmentRegistry(registry as Record<string, string>);
        }
      }
      setSegmentsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [configClient, getToken, id]);

  if (loadError) {
    return (
      <AdminShell title="Contest">
        <p className="font-ui text-[13px] text-danger">{loadError}</p>
      </AdminShell>
    );
  }

  if (contest === null) {
    return <AdminShell title="Contest">Loading…</AdminShell>;
  }

  const pill = contestPillState(contest.state, contest.finalisedAt);
  const slateCount = contest.slate?.marketCount ?? contest.curation?.marketIds.length ?? 0;
  const prizesCount = Object.values(contest.payoutMatrix).reduce<number>((sum, tierMap) => {
    if (!tierMap || typeof tierMap !== 'object') return sum;
    return sum + Object.keys(tierMap).length;
  }, 0);
  // The tab badge is the Contest's own entry count (API-09) — the same number the list row shows,
  // and now the same data the tab renders, so the badge can no longer contradict the tab (CAE-348).
  const entriesTabCount = contest.entryCount;
  const readinessLines = curatedMarkets.map(toReadinessLine);
  const elapsedMs = timeInStatusMs(contest, new Date());
  const ageDuration = elapsedMs === null ? null : formatDuration(elapsedMs);

  return (
    <AdminShell
      title={contest.name}
      meta={
        <>
          <StatusPill state={pill} />
          {ageDuration && <AgeChip text="in this state for" duration={ageDuration} />}
          <IdChip label="ID" value={contest.id.slice(0, 8)} title={contest.id} />
        </>
      }
      actions={
        <>
          <Button variant="ghost" onClick={() => void navigate('/contests')}>
            Back
          </Button>
          {/* Edit is offered on the two not-yet-open statuses — draft and finalised Scheduled.
              Open/Closed (in-flight/settling) and Settled/Canceled (terminal) stay read-only.
              A draft opens the editor directly. A finalised Scheduled contest must first revert
              to draft (dropping the frozen snapshot) before everything is editable again, so its
              Edit routes through a confirm modal. CAE-488. */}
          {pill === 'draft' && canManage && (
            <Button variant="outline" onClick={() => void navigate(`/contests/${contest.id}/edit`)}>
              Edit
            </Button>
          )}
          {pill === 'scheduled' && canManage && (
            <Button variant="outline" onClick={() => setRevertModalOpen(true)}>
              Edit
            </Button>
          )}
          {pill === 'draft' && canManage && (
            <Button disabled={marketsLoading} onClick={() => setFinaliseModalOpen(true)}>
              Finalise
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Tabs
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'slate', label: 'Slate', count: slateCount },
            {
              key: 'prizes',
              label: 'Prizes',
              count: prizesCount,
              attn: pill === 'draft' && prizesCount === 0,
            },
            { key: 'rules', label: 'Rules' },
            { key: 'entries', label: 'Entries', count: entriesTabCount },
            // No count on Awards: API-23 is a keyset page, so the rows we hold are capped at the
            // page size and the contract carries no total. A badge would be right until page two
            // and wrong after — worse than no badge.
            { key: 'awards', label: 'Awards' },
          ]}
          selected={tab}
          onSelect={(key) => setTab(key as TabKey)}
        />
        {tab === 'overview' && (
          <OverviewTab
            contest={contest}
            pill={pill}
            curatedMarkets={readinessLines}
            curatedEvents={curatedEvents}
            marketCounts={countMarketsByEvent(curatedMarkets)}
            segmentRegistry={segmentRegistry}
            readinessLoading={{ markets: marketsLoading, segments: segmentsLoading }}
            onConfigure={(key) => {
              void navigate(`/contests/${contest.id}/edit?step=${readinessEditorStep(key)}`);
            }}
            onOpenSlate={() => void navigate(`/contests/${contest.id}/edit?step=slate`)}
          />
        )}
        {tab === 'slate' && (
          <SlateTab
            contest={contest}
            pill={pill}
            curatedMarkets={curatedMarkets}
            curatedEvents={curatedEvents}
          />
        )}
        {tab === 'prizes' && (
          <PrizesTab
            contest={contest}
            segmentRegistry={segmentRegistry}
            canEdit={pill === 'draft' && canManage}
            onEdit={() => void navigate(`/contests/${contest.id}/edit?step=prizes`)}
          />
        )}
        {tab === 'rules' && <RulesTab contest={contest} />}
        {tab === 'entries' && <ContestEntriesTab contestId={contest.id} client={entriesClient} />}
        {tab === 'awards' && (
          <ContestAwardsTab
            contestId={contest.id}
            client={awardsClient}
            canRetry={canRetryAwards}
          />
        )}
      </div>
      {finaliseModalOpen && (
        <FinaliseModal
          contest={contest}
          curatedMarkets={readinessLines}
          contestsClient={contestsClient}
          onClose={() => setFinaliseModalOpen(false)}
          onFinalised={(updated) => {
            setContest({ ...contest, ...updated });
            setFinaliseModalOpen(false);
          }}
        />
      )}
      {revertModalOpen && (
        <RevertModal
          contest={contest}
          contestsClient={contestsClient}
          onClose={() => setRevertModalOpen(false)}
          onReverted={() => {
            setRevertModalOpen(false);
            void navigate(`/contests/${contest.id}/edit`);
          }}
        />
      )}
    </AdminShell>
  );
}

function countMarketsByEvent(markets: readonly CuratedMarket[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const market of markets) {
    counts[market.eventId] = (counts[market.eventId] ?? 0) + 1;
  }
  return counts;
}

interface OverviewTabProps {
  readonly contest: AdminContestWithEntryCount;
  readonly pill: ReturnType<typeof contestPillState>;
  readonly curatedMarkets: readonly CuratedMarketLine[];
  readonly curatedEvents: readonly CuratedEvent[];
  readonly marketCounts: Record<string, number>;
  readonly segmentRegistry: Record<string, string>;
  readonly readinessLoading: ReadinessLoading;
  readonly onConfigure: (key: ReadinessKey) => void;
  readonly onOpenSlate: () => void;
}

function OverviewTab({
  contest,
  pill,
  curatedMarkets,
  curatedEvents,
  marketCounts,
  segmentRegistry,
  readinessLoading,
  onConfigure,
  onOpenSlate,
}: OverviewTabProps) {
  const rail = buildScheduleRail(contest);
  const readiness =
    pill === 'draft'
      ? computeReadiness(contest, curatedMarkets, segmentRegistry, readinessLoading)
      : null;
  const readinessDone = readiness?.filter((item) => item.done).length ?? 0;
  const kpis = useMemo(
    () => overviewKpis(contest, pill, rail.next?.date),
    [contest, pill, rail.next?.date],
  );

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,11fr)_minmax(0,9fr)]">
      <div className="flex min-w-0 flex-col gap-4">
        {readiness && (
          <Card
            eyebrow="Readiness"
            title="Finalise readiness"
            action={<ProgressRing value={readinessDone} max={readiness.length} />}
          >
            <ChecklistStack className="-mx-3">
              {readiness.map((item) => {
                const editorStep = !item.done ? readinessEditorStep(item.key) : null;
                const editHref = editorStep
                  ? `/contests/${contest.id}/edit?step=${editorStep}`
                  : undefined;
                return (
                  <ChecklistRow
                    key={item.key}
                    done={item.done}
                    label={item.label}
                    hint={item.hint}
                    cta={editHref ? 'Configure' : ''}
                    href={editHref ?? '#'}
                    onClick={
                      editHref
                        ? (event) => {
                            // SPA navigate into the editor on the matching setup step.
                            event.preventDefault();
                            onConfigure(item.key);
                          }
                        : undefined
                    }
                  />
                );
              })}
            </ChecklistStack>
          </Card>
        )}
        {pill === 'scheduled' && (
          <Banner variant="success" title="Finalised — slate and lines are frozen">
            Readiness checks passed. The scheduler will open this contest automatically.
          </Banner>
        )}
        {pill === 'closed' && (
          <Banner variant="info" title="Awaiting settlement">
            Picks are locked. Staged results are maturing before entries settle.
          </Banner>
        )}
        {pill === 'canceled' && (
          <Banner variant="danger" title="Contest canceled">
            This contest was canceled before completion.
          </Banner>
        )}
        <KpiStrip>
          {kpis.map((kpi) => (
            <KpiCell
              key={kpi.label}
              label={kpi.label}
              value={kpi.value}
              idle={kpi.idle}
              foot={kpi.foot}
              bar={kpi.bar}
            />
          ))}
        </KpiStrip>
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        <Card eyebrow="Schedule">
          <LifecycleRail milestones={[...rail.milestones]} next={rail.next} />
        </Card>
        <Card
          eyebrow="Slate"
          action={
            <button
              type="button"
              className="font-ui text-[13px] font-medium text-accent hover:bg-accent-soft rounded-md px-2.5 py-1"
              onClick={onOpenSlate}
            >
              Manage
            </button>
          }
        >
          {curatedEvents.length === 0 ? (
            <p className="font-ui text-[13px] text-ink-4">
              {contest.slate
                ? `${contest.slate.eventCount} events, ${contest.slate.marketCount} props`
                : 'No events curated yet.'}
            </p>
          ) : (
            <div className="flex flex-col">
              {curatedEvents.map((event) => {
                const crests = crestsForEventName(event.name);
                const parts = formatDateParts(event.startTime || null);
                const meta = event.startTime ? `${parts.date} · ${parts.time}` : '—';
                const count = marketCounts[event.id] ?? 0;
                return (
                  <div
                    key={event.id}
                    className="flex items-center gap-3 border-b-[0.5px] border-line py-2.5 first:pt-0 last:border-b-0 last:pb-0"
                  >
                    {crests ? (
                      <CrestPair home={crests.home} away={crests.away} />
                    ) : (
                      <Crest {...lookupTeam(event.name)} />
                    )}
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate font-ui text-sm font-semibold leading-[1.35] tracking-[-0.005em] text-ink">
                        {event.name}
                      </span>
                      <span className="whitespace-nowrap font-ui text-[12.5px] text-ink-4">
                        <span className="font-mono">{meta}</span>
                        {` · ${count} props`}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card eyebrow="Integration" title="Where this lives">
          <DefinitionList>
            <DefinitionRow label="Campaign" value={contest.campaignId} muted />
            <DefinitionRow label="Round" value={contest.round} />
            <DefinitionRow label="Contest ID" value={contest.id} muted />
          </DefinitionList>
        </Card>
      </div>
    </div>
  );
}

interface OverviewKpi {
  readonly label: string;
  readonly value?: string;
  readonly foot: string;
  readonly idle?: boolean;
  readonly bar?: number;
}

function overviewKpis(
  contest: AdminContestWithEntryCount,
  pill: ReturnType<typeof contestPillState>,
  etaOpen: string | undefined,
): readonly OverviewKpi[] {
  const entries = contest.entryCount.toLocaleString('en-US');
  if (pill === 'draft' || pill === 'scheduled') {
    return [
      {
        label: 'Player entries',
        idle: true,
        foot:
          pill === 'scheduled' && etaOpen
            ? `Opens ${etaOpen}`
            : 'No entries yet — contest in draft',
      },
      {
        label: 'Award delivery',
        idle: true,
        foot: 'Begins after contest settles',
      },
    ];
  }
  if (pill === 'open') {
    return [
      {
        label: 'Player entries',
        value: entries,
        foot: 'Live · updating as picks land',
      },
      {
        label: 'Award delivery',
        idle: true,
        foot: 'Begins after contest settles',
      },
    ];
  }
  if (pill === 'closed') {
    return [
      {
        label: 'Player entries',
        value: entries,
        foot: 'Locked at close',
      },
      {
        label: 'Award delivery',
        idle: true,
        foot: 'Begins after contest settles',
      },
    ];
  }
  if (pill === 'settled') {
    return [
      {
        label: 'Player entries',
        value: entries,
        foot: 'Final',
      },
      {
        label: 'Award delivery',
        idle: true,
        foot: 'Delivery metrics land with CAE-269',
      },
    ];
  }
  // canceled
  return [
    {
      label: 'Player entries',
      value: entries,
      foot: 'Voided with contest',
    },
    {
      label: 'Award delivery',
      idle: true,
      foot: 'No awards to deliver',
    },
  ];
}

interface SlateTabProps {
  readonly contest: AdminContestWithEntryCount;
  readonly pill: ReturnType<typeof contestPillState>;
  readonly curatedMarkets: readonly CuratedMarket[];
  readonly curatedEvents: readonly CuratedEvent[];
}

function toVerdictBadge(verdict: NonNullable<CuratedMarket['verdict']>): Verdict {
  return verdict === 'out_of_bounds' ? 'out-of-bounds' : verdict;
}

/**
 * Per-event MarketRow groups. A draft Contest's rows come from the Live catalogue and carry a
 * live Line/verdict/quote; a finalised Contest's rows come frozen from the `contest_*` snapshot
 * (CAE-485) and carry neither. When the snapshot read is genuinely empty (or a draft is
 * uncurated), fall back to the frozen banner + slate counts.
 */
function SlateTab({ contest, pill, curatedMarkets, curatedEvents }: SlateTabProps) {
  const frozen = pill !== 'draft';
  const eventSections = useCollapsibleSections();
  const groupSections = useCollapsibleSections();
  if (curatedMarkets.length === 0) {
    const eventCount = contest.slate?.eventCount ?? contest.curation?.eventIds.length ?? 0;
    const marketCount = contest.slate?.marketCount ?? contest.curation?.marketIds.length ?? 0;
    return (
      <Card title="Slate">
        {frozen && (
          <Banner variant="info" title="Slate frozen at finalisation">
            Lines are locked at the values captured when this contest finalised.
          </Banner>
        )}
        <p className="font-ui text-sm text-ink-2">
          {eventCount} events, {marketCount} props
        </p>
      </Card>
    );
  }

  const eventsById = new Map(curatedEvents.map((event) => [event.id, event]));
  const byEvent = new Map<string, CuratedMarket[]>();
  for (const market of curatedMarkets) {
    const list = byEvent.get(market.eventId) ?? [];
    list.push(market);
    byEvent.set(market.eventId, list);
  }
  const orderedEventIds = (
    curatedEvents.length > 0 ? curatedEvents.map((e) => e.id) : Array.from(byEvent.keys())
  ).filter((eventId) => (byEvent.get(eventId) ?? []).length > 0);

  // Keys for Collapse all: every rendered event, plus every market-type group within it.
  const groupKeyFor = (eventId: string, type: string) => `${eventId}::${type}`;
  const allGroupKeys = orderedEventIds.flatMap((eventId) => {
    const seen = new Set<string>();
    for (const market of byEvent.get(eventId) ?? []) seen.add(market.marketType);
    return Array.from(seen, (type) => groupKeyFor(eventId, type));
  });

  function collapseAll() {
    eventSections.collapse(orderedEventIds);
    groupSections.collapse(allGroupKeys);
  }

  function expandAll() {
    eventSections.expandAll();
    groupSections.expandAll();
  }

  return (
    <Card
      title="Slate"
      action={
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll}>
            Expand all
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            Collapse all
          </Button>
        </div>
      }
    >
      {frozen && (
        <Banner variant="info" title="Slate frozen at finalisation">
          Lines are locked at the values captured when this contest finalised.
        </Banner>
      )}
      <div className="flex flex-col gap-4">
        {orderedEventIds.map((eventId) => {
          const markets = byEvent.get(eventId) ?? [];
          const event = eventsById.get(eventId);
          const name = event?.name ?? eventId;
          const crests = crestsForEventName(name);
          const meta = event?.startTime ? formatEventMeta(event.competition, event.startTime) : '—';
          const byType = new Map<string, CuratedMarket[]>();
          for (const market of markets) {
            const list = byType.get(market.marketType) ?? [];
            list.push(market);
            byType.set(market.marketType, list);
          }
          const eventCollapsed = eventSections.isCollapsed(eventId);
          return (
            <div
              key={eventId}
              className="flex flex-col gap-3 border-t-[0.5px] border-line pt-4 first:border-t-0 first:pt-0"
            >
              <Collapsible
                expanded={!eventCollapsed}
                onToggle={() => eventSections.toggle(eventId)}
                label={`Toggle ${name} props`}
                headerClassName="gap-3"
                header={
                  <>
                    {crests ? (
                      <CrestPair home={crests.home} away={crests.away} />
                    ) : (
                      <Crest {...lookupTeam(name)} />
                    )}
                    <span className="whitespace-nowrap font-ui text-[14.5px] font-semibold leading-[1.3] tracking-[-0.005em] text-ink">
                      {name}
                    </span>
                    <span className="whitespace-nowrap font-mono text-[12.5px] text-ink-4">
                      {meta}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 whitespace-nowrap font-ui text-[12.5px] text-ink-3">
                      <CountBadge count={markets.length} /> props
                    </span>
                  </>
                }
              >
                {Array.from(byType.entries()).map(([type, typed]) => {
                  const groupKey = groupKeyFor(eventId, type);
                  const groupCollapsed = groupSections.isCollapsed(groupKey);
                  return (
                    <div key={type} className="flex flex-col gap-2">
                      <Collapsible
                        expanded={!groupCollapsed}
                        onToggle={() => groupSections.toggle(groupKey)}
                        label={`Toggle ${type} props for ${name}`}
                        header={
                          <>
                            <span className="font-ui text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3">
                              {type}
                            </span>
                            <CountBadge count={typed.length} />
                          </>
                        }
                      >
                        <div className="flex flex-col gap-2">
                          {typed.map((market) => (
                            <MarketRow
                              key={market.id}
                              type={market.marketType}
                              name={market.name}
                              line={market.line.toFixed(1)}
                              // A frozen (finalised) row carries no live verdict/quote — the slate
                              // is immutable, so those slots are simply omitted (CAE-485).
                              verdict={
                                market.verdict ? (
                                  <VerdictBadge verdict={toVerdictBadge(market.verdict)} />
                                ) : undefined
                              }
                              quote={
                                market.quote && hasQuote(market.quote) ? market.quote : undefined
                              }
                              muted={market.verdict === 'suspended'}
                            />
                          ))}
                        </div>
                      </Collapsible>
                    </div>
                  );
                })}
              </Collapsible>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

interface PrizesTabProps {
  readonly contest: AdminContestWithEntryCount;
  readonly segmentRegistry: Record<string, string>;
  readonly canEdit: boolean;
  readonly onEdit: () => void;
}

/** The payout matrix read view (admin-ui.md Prizes tab): rows are correct-pick tiers, columns
 * are segments (Default plus the segment registry's labelled columns). Read-only — the editor
 * (CAE-298) owns writes. */
function PrizesTab({ contest, segmentRegistry, canEdit, onEdit }: PrizesTabProps) {
  const segments = payoutMatrixSegments(segmentRegistry);
  const tiers = new Set<string>();
  for (const segment of segments) {
    const tierMap = contest.payoutMatrix[segment];
    if (tierMap && typeof tierMap === 'object') {
      for (const tier of Object.keys(tierMap)) tiers.add(tier);
    }
  }
  const sortedTiers = Array.from(tiers).sort((a, b) => Number(b) - Number(a));

  return (
    <Card
      eyebrow="Payout matrix"
      title="Prizes by correct picks & segment"
      action={
        canEdit ? (
          <button
            type="button"
            className="rounded-md px-2.5 py-1 font-ui text-[13px] font-medium text-accent hover:bg-accent-soft"
            onClick={onEdit}
          >
            Edit matrix
          </button>
        ) : undefined
      }
    >
      <Table scrollable columns={`140px repeat(${segments.length}, minmax(138px,1fr))`}>
        <THead>
          <TCell>Correct picks</TCell>
          {segments.map((segment) => (
            <TCell key={segment}>
              {segment === 'default' ? 'Default' : segmentRegistry[segment]}
            </TCell>
          ))}
        </THead>
        {sortedTiers.map((tier) => (
          <TRow key={tier}>
            <TCell variant="primary">
              <span className="font-semibold">{tier}</span>
              <span className="text-[12.5px] text-ink-4">correct</span>
            </TCell>
            {segments.map((segment) => {
              const tierMap = contest.payoutMatrix[segment] as Record<string, unknown> | undefined;
              const cell = tierMap?.[tier];
              if (cell === undefined || cell === null || typeof cell !== 'object') {
                return (
                  <TCell key={segment}>
                    <span className="text-ink-4">—</span>
                  </TCell>
                );
              }
              const reward = cell as RewardCell;
              const rewardType = toRewardType(reward.type);
              return (
                <TCell key={segment}>
                  <span className="flex min-w-0 flex-col items-start gap-1">
                    {rewardType ? (
                      <RewardPill
                        type={rewardType}
                        title={REWARD_LABELS[rewardType]}
                        className="max-w-full truncate"
                      />
                    ) : (
                      <span className="font-ui text-[12.5px] font-semibold text-ink-2">
                        {reward.type}
                      </span>
                    )}
                    <span className="font-mono text-[12.5px] text-ink-2">
                      {formatRewardValue(reward)}
                    </span>
                  </span>
                </TCell>
              );
            })}
          </TRow>
        ))}
      </Table>
    </Card>
  );
}

interface RulesTabProps {
  readonly contest: AdminContestWithEntryCount;
}

/** The Contest's snapshotted configuration (INV-08) — already embedded on AdminContest.config,
 * no extra fetch. Read-only per-Contest overrides view (admin-ui.md Rules). */
function RulesTab({ contest }: RulesTabProps) {
  const config = contest.config as {
    minPicks?: number;
    maxPicks?: number;
    lockPolicy?: string;
    voidPolicy?: string;
    pushPolicy?: string;
    resultCorrectionWindowMinutes?: number;
  };
  return (
    <Card eyebrow="Rules" title="Contest configuration">
      <DefinitionList>
        <DefinitionRow label="Pick count" value={`${config.minPicks} – ${config.maxPicks}`} />
        <DefinitionRow label="Lock policy" value={config.lockPolicy} />
        <DefinitionRow label="Void policy" value={config.voidPolicy} />
        <DefinitionRow label="Push policy" value={config.pushPolicy} />
        <DefinitionRow
          label="Correction window (minutes)"
          value={config.resultCorrectionWindowMinutes}
        />
      </DefinitionList>
    </Card>
  );
}

interface RevertModalProps {
  readonly contest: AdminContestWithEntryCount;
  readonly contestsClient: ContestsClient;
  readonly onClose: () => void;
  readonly onReverted: () => void;
}

/**
 * Confirm reverting a finalised Scheduled contest to draft (CAE-488). Editing a finalised
 * contest is only possible as a draft, so this discards the frozen slate/lines snapshot; the
 * operator must finalise again before the scheduler will open it. On success it hands off to the
 * editor.
 */
function RevertModal({ contest, contestsClient, onClose, onReverted }: RevertModalProps) {
  const { getToken } = useAdminAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function submit() {
    const token = getToken();
    if (token === null) return;
    setSubmitting(true);
    setSubmitError(null);
    const result = await contestsClient.revertContest(token, contest.id);
    setSubmitting(false);
    if (result.ok) {
      onReverted();
      return;
    }
    setSubmitError(
      result.message ?? `Could not revert this Contest (${result.code ?? result.status}).`,
    );
  }

  return (
    <Modal open onClose={onClose} maxWidth={580}>
      <ModalHead
        context={
          <>
            <IdChip
              value={contest.id.slice(0, 8)}
              title={contest.id}
              className="border-line bg-surface-muted"
            />
            Revert to draft
          </>
        }
        title="Edit this finalised contest?"
        subtitle="Editing a finalised contest reverts it to draft. The frozen slate and lines are discarded, and you must finalise again before it opens."
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex flex-col gap-3.5 px-7 py-5">
          <Banner variant="warn" title="This discards the frozen snapshot">
            The curated Events and Markets are kept, so the editor reopens on your current slate —
            but the frozen lines are cleared and re-taken at the next finalise.
          </Banner>
          {submitError && <Banner variant="danger">{submitError}</Banner>}
        </div>
      </ModalBody>
      <ModalFoot summary={submitting ? 'Reverting…' : 'Revert to edit'}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={submitting} onClick={() => void submit()}>
          Revert & edit
        </Button>
      </ModalFoot>
    </Modal>
  );
}

interface FinaliseModalProps {
  readonly contest: AdminContestWithEntryCount;
  readonly curatedMarkets: readonly CuratedMarketLine[];
  readonly contestsClient: ContestsClient;
  readonly onClose: () => void;
  readonly onFinalised: (contest: Omit<AdminContestWithEntryCount, 'entryCount'>) => void;
}

/**
 * The GL-16 finalise guard dialog: half-point-line violations block outright (never
 * overridable); a suspended-prop warning may be overridden. Pre-populated from the live
 * catalogue read already used for the readiness checklist, but the submit always calls the real
 * finalise endpoint and reconciles against its 409 in case catalogue state moved in the interim.
 */
function FinaliseModal({
  contest,
  curatedMarkets,
  contestsClient,
  onClose,
  onFinalised,
}: FinaliseModalProps) {
  const { getToken } = useAdminAuth();
  const [override, setOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const preview = previewFinaliseGuards(curatedMarkets);
  const hasBlocking = preview.blocking.length > 0;
  const hasWarnings = preview.warnings.length > 0;

  async function submit() {
    const token = getToken();
    if (token === null) return;
    setSubmitting(true);
    setSubmitError(null);
    const result = await contestsClient.finaliseContest(token, contest.id, override);
    setSubmitting(false);
    if (result.ok) {
      const { ok: _ok, ...updated } = result;
      onFinalised(updated);
      return;
    }
    if (result.code === 'suspended_props') {
      // Catalogue state moved since the pre-check rendered — the live 409 is authoritative.
      setSubmitError(result.message ?? 'One or more curated Markets are currently suspended.');
      return;
    }
    setSubmitError(
      result.message ?? `Could not finalise this Contest (${result.code ?? result.status}).`,
    );
  }

  const canFinalise = !hasBlocking;
  const summary = hasBlocking
    ? `${preview.blocking.length} blocking issue(s)`
    : 'Ready to finalise';

  return (
    <Modal open onClose={onClose} maxWidth={580}>
      <ModalHead
        context={
          <>
            <IdChip
              value={contest.id.slice(0, 8)}
              title={contest.id}
              className="border-line bg-surface-muted"
            />
            Finalise
          </>
        }
        title="Finalise contest"
        subtitle="Finalising freezes the slate and all lines. The scheduler opens the contest at its opens-at time."
        onClose={onClose}
      />
      <ModalBody>
        <div className="flex flex-col gap-3.5 px-7 py-5">
          {hasBlocking && (
            <Banner variant="danger" title="Resolve before finalising">
              <ul className="m-0 list-none p-0">
                {preview.blocking.map((entry) => (
                  <li key={entry.marketId}>
                    {entry.name} — <span className="font-mono">{entry.line}</span>
                  </li>
                ))}
              </ul>
            </Banner>
          )}
          {hasWarnings && (
            <Banner variant="warn" title="Proceed with caution">
              <ul className="m-0 list-none p-0">
                {preview.warnings.map((entry) => (
                  <li key={entry.marketId}>
                    {entry.name} is currently suspended — it will freeze in its current state.
                  </li>
                ))}
              </ul>
              <label className="mt-2 flex items-center gap-2 font-ui text-[13px]">
                <input
                  type="checkbox"
                  checked={override}
                  onChange={(event) => setOverride(event.target.checked)}
                />
                Proceed with caution — finalise despite the suspended prop(s)
              </label>
            </Banner>
          )}
          {canFinalise && (
            <Banner variant="success" title="All readiness checks passed">
              Slate and lines will freeze immediately.
            </Banner>
          )}
          {submitError && <Banner variant="danger">{submitError}</Banner>}
        </div>
      </ModalBody>
      <ModalFoot summary={summary}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={hasBlocking || (hasWarnings && !override) || submitting}
          onClick={() => void submit()}
        >
          Finalise & freeze
        </Button>
      </ModalFoot>
    </Modal>
  );
}
